import React, { useMemo } from "react";
import tailwindConfig from "tailwind.config";
import resolveConfig from "tailwindcss/resolveConfig";

import ChartComparison from "@/features/compare-players/components/organisms/ChartComparison";

import {
  PlayerDetailPlayerMetaFragment,
  WatchlistPlayerFragment,
  WatchlistPlayerSeasonWeightedAvgMetricsFragment,
} from "@/generated/graphql";
import { useSafeFont } from "@/hooks/useSafeFont";

/**
 *
 */
interface Props {
  currentPlayer: PlayerDetailPlayerMetaFragment | null | undefined;
  selectedPlayer: WatchlistPlayerFragment | null;
}

const formatDataMetrics = (
  data: WatchlistPlayerSeasonWeightedAvgMetricsFragment[],
  playerId: string
) => {
  return (data ?? [])
    .filter(m => m?.ggUnifiedSeasonKey && m.seasonalGpm)
    .map(metric => ({
      season: metric?.ggUnifiedSeasonKey,
      [`epm_${playerId}`]: metric?.seasonalGpm!,
    }));
};

/**
 * Container component for Plus/Minus chart comparison between two players
 * Processes seasonal GPM (Goals Plus/Minus) data and renders comparison chart
 * @param root0 - Component props
 * @param root0.currentPlayer - The primary player being viewed (current player detail)
 * @param root0.selectedPlayer - The player being compared against the current player
 */
const PlusMinusChartComparisonContainer = ({
  currentPlayer,
  selectedPlayer,
}: Props) => {
  const font = useSafeFont(12);

  const fullConfig = resolveConfig(tailwindConfig);
  const themeColors = fullConfig.theme.colors;

  const currentPlayerName = useMemo(
    () => currentPlayer?.name ?? "",
    [currentPlayer]
  );
  const selectedPlayerName = useMemo(
    () => selectedPlayer?.name ?? "",
    [selectedPlayer]
  );

  const playerData = useMemo(() => {
    const currentPlayerData = formatDataMetrics(
      currentPlayer?.seasonWeightedAvgMetrics as WatchlistPlayerSeasonWeightedAvgMetricsFragment[],
      currentPlayer?.id ?? ""
    );
    const selectedPlayerData = formatDataMetrics(
      selectedPlayer?.seasonWeightedAvgMetrics as WatchlistPlayerSeasonWeightedAvgMetricsFragment[],
      selectedPlayer?.id ?? ""
    );

    /**
     * Merged season data keyed by season
     * Each season contains EPM values for both players (if available)
     */
    interface SeasonData {
      season: string | number;
      [key: string]: string | number;
    }

    // Merge data by season for Victory chart compatibility using immutable reduce pattern
    const seasonData = [...currentPlayerData, ...selectedPlayerData].reduce(
      (seasons, item) => {
        const existing = seasons[item.season] || { season: item.season };
        return {
          ...seasons,
          [item.season]: { ...existing, ...item },
        };
      },
      {} as Record<string | number, SeasonData>
    );

    return Object.values(seasonData).sort((a, b) =>
      a.season > b.season ? 1 : -1
    );
  }, [
    currentPlayer?.id,
    currentPlayer?.seasonWeightedAvgMetrics,
    selectedPlayer?.id,
    selectedPlayer?.seasonWeightedAvgMetrics,
  ]);

  const yKeys = Array.from(
    new Set(
      playerData.flatMap(d => Object.keys(d).filter(k => k.includes("epm")))
    )
  );

  const colors = yKeys.reduce(
    (acc, key) => {
      return {
        ...acc,
        [key]:
          key === `epm_${currentPlayer?.id ?? ""}`
            ? // @ts-expect-error TODO: figure out the proper way to extend the theme so we don't get typescript errors: https://gluestack.io/ui/docs/home/theme-configuration/customizing-theme
              themeColors.chart.player
            : // @ts-expect-error TODO: figure out the proper way to extend the theme so we don't get typescript errors: https://gluestack.io/ui/docs/home/theme-configuration/customizing-theme
              themeColors.chart.other,
      };
    },
    {} as Record<string, string>
  );

  return (
    <ChartComparison
      title="Plus/Minus"
      data={playerData}
      yKeys={yKeys}
      font={font}
      colors={colors}
      currentPlayerName={currentPlayerName}
      selectedPlayerName={selectedPlayerName}
    />
  );
};

export default PlusMinusChartComparisonContainer;
